import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Link2, Settings, Tag, Eye, Copy, ExternalLink, MapPin, Building2, Users, Handshake, Home, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import SocialPreviewSettings from '@/components/SocialPreviewSettings';
import { FieldHelp, InlineHelpBanner } from '@/components/help';
import { apiV1 } from '@/api/client';
import { useRegionRequired } from '@/lib/partner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEPS = [
  { id: 'destination', title: 'Destination', icon: Link2, description: 'Enter the target URL' },
  { id: 'customize', title: 'Customize', icon: Settings, description: 'Short URL settings' },
  { id: 'reporting', title: 'Reporting', icon: Tag, description: 'Add reporting parameters' },
  { id: 'social', title: 'Social Preview', icon: Globe, description: 'Configure social sharing' },
  { id: 'preview', title: 'Preview', icon: Eye, description: 'Review and create' },
];

export default function CreateShortURL() {
  const regionRequired = useRegionRequired();
  const navigate = useNavigate();
  const { getShortUrlDomain } = useAppSettings();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  
  // Reference data for dropdowns
  const [referenceData, setReferenceData] = useState({
    regions: [],
    offices: [],
    agents: [],
    partnerships: [],
  });
  
  // Filter states
  const [officeTypeFilter, setOfficeTypeFilter] = useState('all');
  
  // Filtered options based on hierarchy
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Destination
    title: '',
    description: '',
    destination_url: '',
    
    // Step 2: Customize
    custom_slug: '',
    use_custom_slug: false,
    expires_at: '',
    password: '',
    use_password: false,
    click_limit: '',
    use_click_limit: false,
    
    // UTM Parameters
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    
    // Step 3: Reporting
    region_id: '',
    region_name: '',
    office_id: '',
    office_name: '',
    agent_id: '',
    agent_name: '',
    partnership_id: '',
    partnership_name: '',
    listing_ref: '',
    
    // Tags
    tags: [],
    
    // Social Preview (Open Graph)
    social_preview: {
      og_title: '',
      og_description: '',
      og_image_url: '',
      og_image_id: '',
      twitter_card_type: 'summary_large_image',
      use_destination_preview: false,
    },
  });

  useEffect(() => {
    fetchReferenceData();
  }, []);

  // Update filtered offices when region or office type filter changes
  useEffect(() => {
    let filtered = referenceData.offices;
    
    // Filter by region (only show offices that have this region_id)
    if (formData.region_id) {
      filtered = filtered.filter(o => o.region_id === formData.region_id);
    }
    
    // Filter by office type
    if (officeTypeFilter !== 'all') {
      filtered = filtered.filter(o => o.office_type === officeTypeFilter);
    }
    
    setFilteredOffices(filtered);
    
    // Reset office selection if it's no longer in the filtered list
    if (formData.office_id) {
      const stillValid = filtered.some(o => o.id === formData.office_id);
      if (!stillValid) {
        setFormData(prev => ({ ...prev, office_id: '', office_name: '' }));
      }
    }
  }, [formData.region_id, officeTypeFilter, referenceData.offices]);

  // Update filtered agents when office changes
  useEffect(() => {
    let filtered = referenceData.agents;
    
    if (formData.office_id) {
      // Show only agents for the selected office
      filtered = filtered.filter(a => a.office_id === formData.office_id);
    } else if (formData.region_id) {
      // If no office selected but region is, show agents from offices in that region
      const regionOfficeIds = referenceData.offices
        .filter(o => o.region_id === formData.region_id)
        .map(o => o.id);
      filtered = filtered.filter(a => regionOfficeIds.includes(a.office_id));
    }
    
    setFilteredAgents(filtered);
    
    // Reset agent selection if it's no longer in the filtered list
    if (formData.agent_id) {
      const stillValid = filtered.some(a => a.id === formData.agent_id);
      if (!stillValid) {
        setFormData(prev => ({ ...prev, agent_id: '', agent_name: '' }));
      }
    }
  }, [formData.office_id, formData.region_id, referenceData.agents, referenceData.offices]);

  const fetchReferenceData = async () => {
    try {
      const { data } = await apiV1.get('/reference-data/options');
      if (data?.success) {
        setReferenceData({
          regions: data.data.region || [],
          offices: data.data.office || [],
          agents: data.data.agent || [],
          partnerships: data.data.partnership || [],
        });
        setFilteredOffices(data.data.office || []);
        setFilteredAgents(data.data.agent || []);
      }
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }

    setCheckingSlug(true);
    try {
      const { data } = await apiV1.get(`/urls/check/${slug}`);
      setSlugAvailable(data?.data?.available);
    } catch (error) {
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'custom_slug') {
      const debounceTimer = setTimeout(() => checkSlugAvailability(value), 500);
      return () => clearTimeout(debounceTimer);
    }
  };

  const handleSelectChange = (idField, id, dataArray, nameField) => {
    const item = dataArray.find(i => i.id === id);
    setFormData(prev => ({
      ...prev,
      [idField]: id,
      [nameField]: item?.name || '',
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.title.trim() && formData.destination_url.trim();
      case 1:
        if (formData.use_custom_slug && formData.custom_slug) {
          return slugAvailable === true;
        }
        return true;
      case 2:
        // Region is mandatory — every record must be linked to at least a region.
        return !regionRequired || !!formData.region_id;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    // A region is mandatory — every record must be linked to at least a region.
    if (regionRequired && !formData.region_id) {
      toast.error('Please select a region before creating this short URL.');
      setCurrentStep(2);
      return;
    }
    setIsSubmitting(true);

    try {
      // Prepare social preview - only include if custom settings are used
      let socialPreview = null;
      if (!formData.social_preview.use_destination_preview) {
        // Check if any custom OG fields are set
        const hasCustomOG = formData.social_preview.og_title || 
                           formData.social_preview.og_description || 
                           formData.social_preview.og_image_url;
        if (hasCustomOG) {
          socialPreview = {
            og_title: formData.social_preview.og_title || null,
            og_description: formData.social_preview.og_description || null,
            og_image_url: formData.social_preview.og_image_url || null,
            og_image_id: formData.social_preview.og_image_id || null,
            twitter_card_type: formData.social_preview.twitter_card_type || 'summary_large_image',
            use_destination_preview: false,
          };
        }
      } else {
        socialPreview = {
          use_destination_preview: true,
        };
      }

      const payload = {
        title: formData.title,
        description: formData.description || null,
        destination_url: formData.destination_url,
        custom_slug: formData.use_custom_slug ? formData.custom_slug : null,
        tracking: {
          region_id: formData.region_id || null,
          region_name: formData.region_name || null,
          office_id: formData.office_id || null,
          office_name: formData.office_name || null,
          agent_id: formData.agent_id || null,
          agent_name: formData.agent_name || null,
          partnership_id: formData.partnership_id || null,
          partnership_name: formData.partnership_name || null,
          listing_ref: formData.listing_ref || null,
        },
        utm: {
          utm_source: formData.utm_source || null,
          utm_medium: formData.utm_medium || null,
          utm_campaign: formData.utm_campaign || null,
          utm_term: formData.utm_term || null,
          utm_content: formData.utm_content || null,
        },
        expires_at: formData.use_click_limit && formData.expires_at ? formData.expires_at : null,
        password: formData.use_password ? formData.password : null,
        click_limit: formData.use_click_limit && formData.click_limit ? parseInt(formData.click_limit) : null,
        tags: formData.tags,
        social_preview: socialPreview,
      };
      
      const token = localStorage.getItem('token');
      let data;
      try {
        const response = await apiV1.post('/urls/', payload);
        data = response.data;
      } catch (error) {
        const message =
          error?.message ||
          error?.response?.data?.detail?.message ||
          error?.response?.data?.detail ||
          'Failed to create short URL';
        throw new Error(message);
      }

      if (data?.success) {
        toast.success('Short URL created successfully!');
        navigate('/urls');
      } else {
        throw new Error(data?.detail || 'Failed to create short URL');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create short URL');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Build preview URL using dynamic domain from settings
  const shortUrlDomain = getShortUrlDomain();
  const previewShortUrl = formData.use_custom_slug && formData.custom_slug
    ? `${shortUrlDomain}/${formData.custom_slug}`
    : `${shortUrlDomain}/[auto-generated]`;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="create-url-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/urls')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Create Short URL</h1>
          <p className="text-muted-foreground">Create a trackable short link</p>
        </div>
      </div>

      {/* Orientation banner — dismissible per-user, never reappears */}
      <InlineHelpBanner id="create-short-url-intro" title="How short URLs work">
        Every short URL points to a destination you already have (a property
        listing, a landing page, anywhere on the web). We track every click
        and add tracking parameters (UTM) so you can see where traffic came
        from in the Analytics page later.
      </InlineHelpBanner>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, idx) => (
          <React.Fragment key={step.id}>
            <div 
              className={`flex items-center gap-2 cursor-pointer ${idx <= currentStep ? 'text-primary' : 'text-muted-foreground'}`}
              onClick={() => idx < currentStep && setCurrentStep(idx)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                idx < currentStep ? 'bg-primary text-primary-foreground' :
                idx === currentStep ? 'bg-primary/20 text-primary border-2 border-primary' :
                'bg-muted text-muted-foreground'
              }`}>
                {idx < currentStep ? <Check className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-medium">{step.title}</div>
                <div className="text-xs text-muted-foreground">{step.description}</div>
              </div>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-4 ${idx < currentStep ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Destination */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Summer Property Listing"
                  value={formData.title}
                  onChange={(e) => updateForm('title', e.target.value)}
                  data-testid="title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="destination_url">Destination URL *</Label>
                <Input
                  id="destination_url"
                  type="url"
                  placeholder="https://pamgolding.co.za/property/123456"
                  value={formData.destination_url}
                  onChange={(e) => updateForm('destination_url', e.target.value)}
                  data-testid="destination-url-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of this link..."
                  value={formData.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 2: Customize */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Custom Slug */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Custom Short Code</Label>
                      <FieldHelp text="The custom short code (or 'slug') is the part after the slash in the short URL. Lowercase letters, numbers, and dashes only. Memorable codes get more clicks than random ones — but every code has to be unique." />
                    </div>
                    <p className="text-sm text-muted-foreground">Use a memorable code instead of random</p>
                  </div>
                  <Switch
                    checked={formData.use_custom_slug}
                    onCheckedChange={(checked) => updateForm('use_custom_slug', checked)}
                  />
                </div>
                
                {formData.use_custom_slug && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{shortUrlDomain}/</span>
                      <Input
                        placeholder="my-custom-code"
                        value={formData.custom_slug}
                        onChange={(e) => updateForm('custom_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        className="flex-1"
                      />
                    </div>
                    {checkingSlug && <p className="text-sm text-muted-foreground">Checking availability...</p>}
                    {slugAvailable === true && <p className="text-sm text-green-600">✓ Available</p>}
                    {slugAvailable === false && <p className="text-sm text-red-600">✗ Already taken</p>}
                  </div>
                )}
              </div>

              {/* UTM Parameters */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label>UTM Parameters (optional)</Label>
                  <FieldHelp text="UTM parameters are tags that get added to the URL and follow each click into your analytics. They let you split traffic by where it came from (source), what kind of placement (medium), and which campaign it belonged to. Fill in only what's relevant — every empty field is omitted." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="utm_source" className="text-sm">Source</Label>
                      <FieldHelp text="Where the click came from — e.g. 'newsletter', 'instagram', 'gumtree'." />
                    </div>
                    <Input
                      id="utm_source"
                      placeholder="e.g., newsletter"
                      value={formData.utm_source}
                      onChange={(e) => updateForm('utm_source', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="utm_medium" className="text-sm">Medium</Label>
                      <FieldHelp text="What kind of placement — e.g. 'email', 'sms', 'cpc' (paid), 'social' (organic)." />
                    </div>
                    <Input
                      id="utm_medium"
                      placeholder="e.g., email"
                      value={formData.utm_medium}
                      onChange={(e) => updateForm('utm_medium', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="utm_campaign" className="text-sm">Campaign</Label>
                      <FieldHelp text="The named campaign this link belongs to — e.g. 'spring-sale-2026'. Reuse the same campaign value across every link you send out for that campaign." />
                    </div>
                    <Input
                      id="utm_campaign"
                      placeholder="e.g., spring-sale"
                      value={formData.utm_campaign}
                      onChange={(e) => updateForm('utm_campaign', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="utm_term" className="text-sm">Term</Label>
                      <FieldHelp text="The keyword or audience the link is targeted at — usually only set for paid search." />
                    </div>
                    <Input
                      id="utm_term"
                      placeholder="e.g., luxury-homes"
                      value={formData.utm_term}
                      onChange={(e) => updateForm('utm_term', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="utm_content" className="text-sm">Content</Label>
                      <FieldHelp text="Distinguishes two links in the same campaign — e.g. 'header-link' vs 'footer-link' in the same email, so you can see which one performed better." />
                    </div>
                    <Input
                      id="utm_content"
                      placeholder="header-link"
                      value={formData.utm_content}
                      onChange={(e) => updateForm('utm_content', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Advanced Options */}
              <div className="space-y-4 pt-4 border-t">
                <Label>Advanced Options</Label>
                
                {/* Password Protection */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Password Protection</Label>
                      <FieldHelp text="When on, anyone following the link is asked for the password before being redirected. Use for private property packs or pre-launch material." />
                    </div>
                    <p className="text-sm text-muted-foreground">Require password to access</p>
                  </div>
                  <Switch
                    checked={formData.use_password}
                    onCheckedChange={(checked) => updateForm('use_password', checked)}
                  />
                </div>
                {formData.use_password && (
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => updateForm('password', e.target.value)}
                  />
                )}

                {/* Click Limit */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Click Limit</Label>
                      <FieldHelp text="After this many clicks the link stops working — useful for limited-availability offers or to cap exposure on a paid placement. The link can be re-enabled later from its detail page." />
                    </div>
                    <p className="text-sm text-muted-foreground">Disable after X clicks</p>
                  </div>
                  <Switch
                    checked={formData.use_click_limit}
                    onCheckedChange={(checked) => updateForm('use_click_limit', checked)}
                  />
                </div>
                {formData.use_click_limit && (
                  <Input
                    type="number"
                    placeholder="e.g., 1000"
                    value={formData.click_limit}
                    onChange={(e) => updateForm('click_limit', e.target.value)}
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 3: Reporting */}
          {currentStep === 2 && !regionRequired && (
            <p className="text-muted-foreground" data-testid="tracking-national-note">
              This will be national Pam Golding Properties content. Partner accounts don't link records to a region or office.
            </p>
          )}
          {currentStep === 2 && regionRequired && (
            <div className="space-y-6">
              <p className="text-muted-foreground">
                Link this short URL to the part of the business it belongs to. A
                region is required; office, agent and partnership are optional.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Region */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    Region <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.region_id || "none"}
                    onValueChange={(val) => handleSelectChange('region_id', val === "none" ? "" : val, referenceData.regions, 'region_name')}
                  >
                    <SelectTrigger data-testid="tracking-region-select" aria-required="true">
                      <SelectValue placeholder="Select a region (required)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {referenceData.regions.map((region) => (
                        <SelectItem key={region.id} value={region.id}>
                          {region.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!formData.region_id && (
                    <p className="text-xs text-muted-foreground">
                      Required — you can only pick regions you have access to.
                    </p>
                  )}
                </div>

                {/* Office Type Filter */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    Office Type
                  </Label>
                  <Select
                    value={officeTypeFilter}
                    onValueChange={(value) => setOfficeTypeFilter(value)}
                  >
                    <SelectTrigger data-testid="tracking-office-type-select">
                      <SelectValue placeholder="Filter by office type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="branch">Branch</SelectItem>
                      <SelectItem value="franchise">Franchise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Office */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    Office
                    {filteredOffices.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ({filteredOffices.length} available)
                      </span>
                    )}
                  </Label>
                  <Select 
                    value={formData.office_id || "none"} 
                    onValueChange={(val) => handleSelectChange('office_id', val === "none" ? "" : val, filteredOffices, 'office_name')}
                  >
                    <SelectTrigger data-testid="tracking-office-select">
                      <SelectValue placeholder="Select office" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredOffices.map((office) => (
                        <SelectItem key={office.id} value={office.id}>
                          {office.name}
                          {office.office_type && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({office.office_type})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.region_id && filteredOffices.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No offices found for the selected region{officeTypeFilter !== 'all' ? ` and type "${officeTypeFilter}"` : ''}
                    </p>
                  )}
                </div>

                {/* Agent */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    Agent
                    {filteredAgents.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ({filteredAgents.length} available)
                      </span>
                    )}
                  </Label>
                  <Select 
                    value={formData.agent_id || "none"} 
                    onValueChange={(val) => handleSelectChange('agent_id', val === "none" ? "" : val, filteredAgents, 'agent_name')}
                  >
                    <SelectTrigger data-testid="tracking-agent-select">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredAgents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.office_id && filteredAgents.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No agents found for the selected office
                    </p>
                  )}
                </div>

                {/* Partnership */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Handshake className="h-4 w-4 text-primary" />
                    Partnership
                  </Label>
                  <Select 
                    value={formData.partnership_id || "none"} 
                    onValueChange={(val) => handleSelectChange('partnership_id', val === "none" ? "" : val, referenceData.partnerships, 'partnership_name')}
                  >
                    <SelectTrigger data-testid="tracking-partnership-select">
                      <SelectValue placeholder="Select partnership" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {referenceData.partnerships.map((partnership) => (
                        <SelectItem key={partnership.id} value={partnership.id}>
                          {partnership.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Listing Reference */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-primary" />
                    Listing Reference
                  </Label>
                  <Input
                    placeholder="e.g., PG-2024-001"
                    value={formData.listing_ref}
                    onChange={(e) => updateForm('listing_ref', e.target.value)}
                    data-testid="tracking-listing-ref-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Social Preview */}
          {currentStep === 3 && (
            <SocialPreviewSettings
              value={formData.social_preview}
              onChange={(preview) => updateForm('social_preview', preview)}
              assetName={formData.title}
              destinationUrl={formData.destination_url}
            />
          )}

          {/* Step 5: Preview */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-muted-foreground">Short URL</div>
                    <div className="text-lg font-mono font-medium">{previewShortUrl}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(`https://${previewShortUrl}`)}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground">Redirects to</div>
                  <div className="text-sm font-mono truncate">{formData.destination_url}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Details</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Title</dt>
                      <dd>{formData.title}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Type</dt>
                      <dd>{formData.use_custom_slug ? 'Custom Slug' : 'Random Code'}</dd>
                    </div>
                    {formData.use_password && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Password</dt>
                        <dd>Protected</dd>
                      </div>
                    )}
                    {formData.use_click_limit && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Click Limit</dt>
                        <dd>{formData.click_limit}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Reporting</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.region_name && <Badge variant="outline">{formData.region_name}</Badge>}
                    {formData.office_name && <Badge variant="outline">{formData.office_name}</Badge>}
                    {formData.agent_name && <Badge variant="outline">{formData.agent_name}</Badge>}
                    {formData.partnership_name && <Badge variant="outline">{formData.partnership_name}</Badge>}
                    {formData.listing_ref && <Badge variant="secondary">{formData.listing_ref}</Badge>}
                    {!formData.region_name && !formData.office_name && !formData.agent_name && !formData.partnership_name && !formData.listing_ref && (
                      <span className="text-sm text-muted-foreground">No tracking parameters</span>
                    )}
                  </div>
                </div>
              </div>

              {(formData.utm_source || formData.utm_medium || formData.utm_campaign) && (
                <div>
                  <h4 className="font-medium mb-2">UTM Parameters</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.utm_source && <Badge variant="secondary">source: {formData.utm_source}</Badge>}
                    {formData.utm_medium && <Badge variant="secondary">medium: {formData.utm_medium}</Badge>}
                    {formData.utm_campaign && <Badge variant="secondary">campaign: {formData.utm_campaign}</Badge>}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {currentStep < STEPS.length - 1 ? (
          <Button
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
            data-testid="next-step-button"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canProceed()}
            data-testid="create-url-button"
          >
            {isSubmitting ? 'Creating...' : 'Create Short URL'}
          </Button>
        )}
      </div>
    </div>
  );
}
